<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="isCreate ? null : fullName(dealer)" :back="listPath">
            <v-btn v-if="isShow" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="dealer.firstName" :rules="required" label="First name *" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="dealer.lastName" :rules="required" label="Last name *" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.trim="dealer.email"
                                :rules="[...required, ...emailRules]"
                                label="Email *"
                                hint="Also used to sign in"
                                prepend-inner-icon="mdi-email-outline"
                                outlined
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <date-field v-model="dealer.birthDate" label="Birth date" :readonly="readOnly" />
                        </v-col>
                        <v-col v-if="!readOnly" cols="12" md="6">
                            <v-text-field
                                v-model="password"
                                :rules="isCreate ? passwordRules : []"
                                :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                :type="showPassword ? 'text' : 'password'"
                                :label="isCreate ? 'Password *' : 'New password'"
                                :hint="isEdit ? 'Leave empty to keep the current password' : ''"
                                persistent-hint
                                autocomplete="new-password"
                                prepend-inner-icon="mdi-lock-outline"
                                outlined
                                @click:append="showPassword = !showPassword"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create dealer' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import dealerService from '@/services/dealer-service'
    import formPage from '@/mixins/form-page'
    import PageHeader from '@/components/PageHeader'
    import DateField from '@/components/DateField'
    import { fullName, toInputDate } from '@/utils/format'

    export default {
        name: 'DealerForm',
        components: { PageHeader, DateField },
        mixins: [formPage],
        data: () => {
            return {
                entityLabel: 'Dealer',
                dealerId: null,
                showPassword: false,
                password: '',
                dealer: {
                    firstName: '',
                    lastName: '',
                    birthDate: null,
                    email: ''
                },
                emailRules: [
                    v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Enter a valid email address'
                ],
                passwordRules: [
                    v => !!v || 'This field is required'
                ]
            }
        },
        created() {
            if (this.isCreate) {
                return
            }
            this.dealerId = parseInt(this.$route.params.dealerId)
            this.loadingEntity = true
            dealerService.getDealerById(this.dealerId).then(resp => {
                const dealer = resp.data.dealer
                // DealerType only accepts YYYY-MM-DD, the API returns a full timestamp
                this.dealer = { ...this.dealer, ...dealer, birthDate: toInputDate(dealer.birthDate) }
            }).catch(error => {
                this.$notify.error(error, 'Could not load the dealer')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            fullName,
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                const payload = {
                    firstName: this.dealer.firstName,
                    lastName: this.dealer.lastName,
                    email: this.dealer.email,
                    birthDate: this.dealer.birthDate,
                    // on edit an empty password keeps the current one
                    password: this.password
                }
                const request = this.isEdit
                    ? dealerService.editDealerById(this.dealerId, payload)
                    : dealerService.createDealer(payload)
                request.then(() => {
                    this.$notify.success(this.isCreate ? 'Dealer created' : 'Dealer saved')
                    this.goBack()
                }).catch(error => {
                    this.$notify.error(error, 'Could not save the dealer')
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>
